// src/utils/imageProcessing.ts
//
// Client-side photo prep BEFORE upload. Two responsibilities only:
//   1. Strip EXIF metadata (privacy — GPS tags must not leave the
//      device baked into the file)
//   2. Cap the longest edge at 3072px so uploads don't balloon on
//      poor cellular connections
//
// We do NOT generate a separate "vision" derivative. The user uploads
// ONE file. The backend pulls it from Storage server-side, resizes
// in-memory before sending to vision APIs, and otherwise leaves the
// archival copy untouched. This means the user's bandwidth pays for
// exactly one upload per photo.
//
// Why 3072px and not native?
// ─────────────────────────
// 3072px on the long edge is ~9 megapixels — visually indistinguishable
// from a 12MP iPhone capture at any reasonable display size. File size
// drops from 6-12MB to ~2-4MB at q=0.92. That difference matters when
// a user is mid-street with two bars of LTE.
//
// Quality-vs-size sweet spot for a travel app where the photo is
// the artifact, not raw evidence.

import imageCompression from 'browser-image-compression';

const MAX_EDGE = 3072;
const QUALITY = 0.92;
const MAX_MB = 5; // Hard ceiling — anything larger gets stronger compression

/**
 * Strip EXIF (and any embedded GPS tags) by re-decoding into a fresh
 * File. browser-image-compression handles this when re-encoding;
 * passing a fresh File wrapper ensures any prior arrayBuffer metadata
 * isn't carried forward.
 */
async function stripExif(file: File): Promise<File> {
  const buffer = await file.arrayBuffer();
  return new File([buffer], file.name, {
    type: file.type,
    lastModified: file.lastModified,
  });
}

export const processImage = async (file: File): Promise<File> => {
  const cleaned = await stripExif(file);

  try {
    const compressed = await imageCompression(cleaned, {
      maxSizeMB: MAX_MB,
      maxWidthOrHeight: MAX_EDGE,
      // Run compression on the main thread instead of in a Web
      // Worker. The library's worker calls `importScripts()` to
      // load its own implementation from
      // `cdn.jsdelivr.net/npm/browser-image-compression@2.0.2/...`
      // — which violates our `script-src` CSP and silently fails
      // (the worker errors, the catch path falls back to the
      // original file). Disabling the worker keeps the same
      // compression logic running synchronously on the page.
      //
      // Trade-off: a 5MB image takes ~300–800ms instead of being
      // off the main thread. Acceptable for a one-photo-at-a-
      // time UX. The alternative — allowlisting jsdelivr.net in
      // CSP — would let any package on jsdelivr be loaded as a
      // script from our origin, which is too broad. (We could
      // also self-host the script, but maintaining a copy of
      // a versioned third-party library defeats the simplicity
      // win of using the library.)
      useWebWorker: false,
      fileType: 'image/jpeg',
      initialQuality: QUALITY,
    });

    return new File([compressed], file.name, {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
  } catch (err) {
    console.error('Error processing image, falling back to cleaned file:', err);
    // If compression fails entirely, at least we've stripped EXIF.
    return cleaned;
  }
};

export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export const validateImageFile = (file: File): { valid: boolean; error?: string } => {
  const validTypes = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/heic',
    'image/heif',
  ];
  const maxSize = 50 * 1024 * 1024; // 50MB raw upload ceiling

  if (!validTypes.some((t) => file.type === t || file.type.startsWith('image/'))) {
    return {
      valid: false,
      error: 'File must be an image (JPG, PNG, WebP, or HEIC)',
    };
  }
  if (file.size > maxSize) {
    return {
      valid: false,
      error: 'Image must be smaller than 50MB',
    };
  }
  return { valid: true };
};
